import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Container, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'

import {
  allGridsDefaults,
  Divider,
  Entity,
  getCurrentPaginationModel,
  getCurrentSortModel,
  getOrderBy,
  getSkip,
  getTop,
  type GridName,
  type IKlantLimiet,
  ThemeConfig,
  useFetchMany,
  useGridStore,
} from 'src/common'

import { ClientLimitsFilter, ClientLimitsGrid } from '../components'
import { useClientLimitsStore } from '../stores'

export default function ClientLimits(): JSX.Element {
  const { CACHE_KEY } = allGridsDefaults.clientLimits
  const GRID_NAME: GridName = 'clientLimits'
  const { t } = useTranslation()
  const {
    mustRefreshClientLimitsData,
    resetClientLimitsStoreState: resetClientLimitsState,
    setMustRefreshClientLimitsData,
  } = useClientLimitsStore()
  const { setGridChanged, setShouldPaginationBeReset } = useGridStore()
  const [filter, setFilter] = useState<string>(undefined)

  const updateFilter = (value: string) => {
    setFilter(value)
    setShouldPaginationBeReset(true)
    setGridChanged(GRID_NAME)
  }

  const refresh = useCallback(
    () => setMustRefreshClientLimitsData(true),
    [setMustRefreshClientLimitsData]
  )

  const resetGridState = useCallback(() => {
    refresh()
    setShouldPaginationBeReset(true)
    setGridChanged(GRID_NAME)
  }, [refresh, setGridChanged, setShouldPaginationBeReset])

  const resetFilter = () => {
    setFilter(undefined)
    setShouldPaginationBeReset(true)
    setGridChanged(GRID_NAME)
  }

  const queryFilter = {
    top: getTop(getCurrentPaginationModel(CACHE_KEY)),
    skip: getSkip(getCurrentPaginationModel(CACHE_KEY)),
    count: true,
    orderby: getOrderBy(getCurrentSortModel(CACHE_KEY)),
    filter: filter?.length > 0 ? filter : undefined,
  }

  const {
    data: result,
    isLoading,
    refetch,
  } = useFetchMany<IKlantLimiet>(queryFilter, Entity.IKlantLimiet)

  useEffect(() => {
    resetClientLimitsState()
    resetGridState()
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  useEffect(() => {
    if (mustRefreshClientLimitsData) {
      refetch().then(() => {
        setMustRefreshClientLimitsData(false)
      })
    }
  }, [mustRefreshClientLimitsData, refetch, setMustRefreshClientLimitsData])

  return (
    <Container maxWidth='xl' sx={{ maxWidth: '1920px !important' }}>
      <Grid container sx={{ width: '100%' }}>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant='h1'
            sx={{
              fontSize: '2.5rem',
              my: `${ThemeConfig.spacing.sm * 8}px`,
            }}
          >
            {t('common.titles.clientLimitsReadOnly')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }} mt={2}>
          <ClientLimitsFilter
            updateFilterString={updateFilter}
            resetFilterString={resetFilter}
          />
        </Grid>
        <Divider {...{ marginTop: 2, width: '100%' }} />
        <Grid size={{ xs: 12 }} mt={2}>
          <ClientLimitsGrid
            isLoading={isLoading}
            refresh={refresh}
            rows={result?.records ?? []}
            totalCount={result?.count ?? 0}
          />
        </Grid>
      </Grid>
    </Container>
  )
}
